import type {
  AggregatedChatResult,
  ChatChunkPayload,
  ClarifyingQuestion,
  FilterOption,
  SSEFiltersPayload
} from '@/interfaces/AudienceManifest.interface';

const MAX_CLARIFYING_OPTIONS = 4;

export function parseSSELine(line: string): ChatChunkPayload | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('data:')) return null;

  const jsonText = trimmed.slice(5).trim();
  if (!jsonText) return null;

  try {
    return JSON.parse(jsonText) as ChatChunkPayload;
  } catch {
    return null;
  }
}

export function looksLikeSegmentSql(text: string): boolean {
  return /\bSELECT\b[\s\S]*\bFROM\b/i.test(text.trim());
}

function normalizeSqlCandidate(text: string): string | null {
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```sql\s*([\s\S]*?)```$/i)?.[1]?.trim();
  const candidate = fenced ?? trimmed;

  return candidate && looksLikeSegmentSql(candidate) ? candidate : null;
}

export function extractSegmentSql(contentParts: string[]): string | null {
  for (let index = contentParts.length - 1; index >= 0; index -= 1) {
    const sql = normalizeSqlCandidate(contentParts[index] ?? '');
    if (sql) {
      return sql;
    }
  }

  const joined = contentParts.join('');
  const fencedSql = joined.match(/```sql\s*([\s\S]*?)```/i)?.[1]?.trim();
  if (fencedSql && looksLikeSegmentSql(fencedSql)) {
    return fencedSql;
  }

  const trimmed = joined.trim();
  if (trimmed && looksLikeSegmentSql(trimmed)) {
    return trimmed;
  }

  return null;
}

function getFilterOptionLabel(option: FilterOption): string | null {
  const label = option.label?.trim() || option.value?.trim();
  return label || null;
}

export function createEmptyResponseClarifyingQuestion(
  message: string,
  filters: SSEFiltersPayload | null
): ClarifyingQuestion {
  const options: string[] = [];
  const seenOptions = new Set<string>();

  for (const property of filters?.properties ?? []) {
    for (const option of property.options ?? []) {
      const label = getFilterOptionLabel(option);
      if (!label || seenOptions.has(label)) continue;

      seenOptions.add(label);
      options.push(label);

      if (options.length >= MAX_CLARIFYING_OPTIONS) {
        return { question: message, options };
      }
    }
  }

  return { question: message, options };
}

function limitClarifyingQuestionOptions(
  question: ClarifyingQuestion
): ClarifyingQuestion {
  return {
    ...question,
    options: question.options.slice(0, MAX_CLARIFYING_OPTIONS)
  };
}

export function aggregateChatChunks(
  chunks: ChatChunkPayload[]
): AggregatedChatResult {
  let filters: SSEFiltersPayload | null = null;
  let clarifyingQuestion: ClarifyingQuestion | null = null;
  let error: string | null = null;
  let sessionId: string | null = null;
  let emptyResponseMessage: string | null = null;
  const contentParts: string[] = [];

  for (const chunk of chunks) {
    if (chunk.session_id) {
      sessionId = chunk.session_id;
    }
    if (chunk.type === 'filters' && chunk.filters) {
      filters = chunk.filters;
    }
    if (chunk.type === 'content' && chunk.text) {
      contentParts.push(chunk.text);
    }
    if (chunk.type === 'empty' && chunk.text) {
      emptyResponseMessage = chunk.text.trim();
    }
    if (chunk.type === 'clarifying_question' && chunk.clarifying_question) {
      clarifyingQuestion = limitClarifyingQuestionOptions(
        chunk.clarifying_question
      );
    }
    if (chunk.type === 'error' && chunk.text) {
      error = chunk.text;
    }
  }

  const content = contentParts.join('').trim();
  const segmentSql = extractSegmentSql(contentParts);
  const resolvedClarifyingQuestion =
    clarifyingQuestion ??
    (emptyResponseMessage
      ? createEmptyResponseClarifyingQuestion(emptyResponseMessage, filters)
      : null);
  const assistantMessage =
    content && !segmentSql && !resolvedClarifyingQuestion ? content : null;

  return {
    sessionId,
    filters,
    segmentSql,
    clarifyingQuestion: resolvedClarifyingQuestion,
    assistantMessage,
    error
  };
}

function yieldPaint(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

export async function streamChatChunks(
  body: ReadableStream<Uint8Array>,
  onChunk?: (chunk: ChatChunkPayload) => void
): Promise<AggregatedChatResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const chunks: ChatChunkPayload[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const chunk = parseSSELine(line);
      if (chunk) {
        chunks.push(chunk);
        onChunk?.(chunk);
        if (onChunk) await yieldPaint();
      }
    }
  }

  if (buffer.trim()) {
    const chunk = parseSSELine(buffer);
    if (chunk) {
      chunks.push(chunk);
      onChunk?.(chunk);
    }
  }

  return aggregateChatChunks(chunks);
}
